/// <amd-module name="Test/TrendChartView/Stores/TrendChartStore" />
import { DurationNormalizer } from "Analytics/Shared/TrendChart/Common/DurationNormalizer";
import { INormalizedDuration } from "Analytics/Shared/TrendChart/Common/Types";
import { ReportConfigurationDefinitions } from "Test/Analytics/Common/Definitions";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { ConfigurationUtility } from "Test/Analytics/Common/Utils/ConfigurationUtility";
import { StringFormattingUtility } from "Test/Analytics/Common/Utils/StringFormattingUtility";
import { TrendChartHelper } from "Test/Analytics/TrendChartCommon/TrendChartHelper";
import { ITrendChartAggregateData, ITrendChartData } from "Test/Analytics/TrendChartCommon/Types";
import { FilterMetadataActions } from "Test/MetadataHandler/Actions/FilterMetadataActions";
import { TrendChartActions } from "Test/TrendChartView/Actions/TrendChartActions";
import * as Resources from "Test/TrendChartView/Resources";
import * as Utils_String from "VSS/Core/Util/String";
import { ChartProps } from "VSS/Features/Charts/Contracts";
import { IEventHandler, Store } from "VSS/Reflux/Store";

export interface ITrendChartState {
    metricsOptions?: { [key: number]: string };
    metricSelected?: CommonTypes.Metric;
    chartOptions?: ChartProps;
    errorText?: string;
}

export interface ITrendChartStore {
    getState(): ITrendChartState;
    addChangedListener(handler: IEventHandler): void;
    removeChangedListener(handler: IEventHandler): void;
}

export class TrendChartStore extends Store implements ITrendChartStore {
    private _state: ITrendChartState;
    private _confValues: CommonTypes.IReportConfiguration;
    private _reportConfigurationDef: ReportConfigurationDefinitions;
    private _trendChartHelper: TrendChartHelper;
    private _chartData: ITrendChartData;
    private _emptyString = "";
    private _stageIdToNameMap: { [key: string]: string };

    constructor(private _actions: TrendChartActions, private _filterMetadataActions: FilterMetadataActions) {
        super("");
        this._initialize();
    }

    public getState(): ITrendChartState {
        return this._state;
    }

    public getConfValues(): CommonTypes.IReportConfiguration {
        return this._confValues;
    }

    public dispose(): void {
        this._actions.updateConfigurationValuesAction.removeListener(this._onUpdateConfiguration);
        this._actions.updateTrendChartMetricAction.removeListener(this._onUpdateTrendChartMetric);
        this._actions.updateTrendChartAction.removeListener(this._onUpdateTrendChart);
        this._filterMetadataActions.updateStageIdToNameMapAction.removeListener(this._onUpdateStageIdToNameMap);
    }

    private _initialize(): void {
        this._reportConfigurationDef = new ReportConfigurationDefinitions();
        this._state = {
            metricsOptions: this._reportConfigurationDef.getChartMetricsProps().options,
            metricSelected: this._reportConfigurationDef.defaultChartMetricValue
        } as ITrendChartState;

        this._trendChartHelper = new TrendChartHelper();

        this._actions.updateConfigurationValuesAction.addListener(this._onUpdateConfiguration);
        this._actions.updateTrendChartMetricAction.addListener(this._onUpdateTrendChartMetric);
        this._actions.updateTrendChartAction.addListener(this._onUpdateTrendChart);
        this._filterMetadataActions.updateStageIdToNameMapAction.addListener(this._onUpdateStageIdToNameMap);
    }

    private _onUpdateConfiguration = (confValues: CommonTypes.IReportConfiguration) => {
        this._state.chartOptions = undefined; // This is done since after config change it will take time for data to come and component can show loading experience till then.
        this._confValues = { ...confValues };

        // Based on group by update metric for chart. With groupBy not None: Result count and pass rate not supported.
        switch (this._confValues.groupBy) {
            case CommonTypes.GroupBy.None:
                this._state.metricsOptions = this._reportConfigurationDef.getChartMetricsProps().options;
                break;
            case CommonTypes.GroupBy.Container:
            case CommonTypes.GroupBy.Owner:
            case CommonTypes.GroupBy.Priority:
            case CommonTypes.GroupBy.Environment:
            case CommonTypes.GroupBy.Branch:
            case CommonTypes.GroupBy.TestRun:
                const metricOptions: { [key: number]: string } = {};
                const allMetricOptions = this._reportConfigurationDef.getChartMetricsProps().options;
                Object.keys(allMetricOptions).forEach(k => {
                    if (k !== CommonTypes.Metric.ResultCountAndPassRate.toString()) {
                        metricOptions[Number(k)] = allMetricOptions[Number(k)];
                    }
                });

                this._state.metricsOptions = metricOptions;

                // Update selected metric if it is not supported with groupBy
                if (this._state.metricSelected === CommonTypes.Metric.ResultCountAndPassRate) {
                    this._state.metricSelected = CommonTypes.Metric.ResultCount;
                }
                break;
        }

        this.emitChanged();
    };

    private _onUpdateTrendChartMetric = (selectedMetric: CommonTypes.Metric) => {
        this._state.chartOptions = undefined;
        this._state.metricSelected = selectedMetric;
        this.emitChanged();
    };

    private _onUpdateTrendChart = (chartData: ITrendChartData) => {
        this._chartData = chartData;
        if (
            this._confValues &&
            !ConfigurationUtility.areConfValuesEqual(this._confValues, this._chartData.confValues) &&
            this._chartData.metricSelected === this._state.metricSelected
        ) {
            // Do not update store as the configuration has changed.
            return;
        }

        if (!(this._confValues && this._confValues.groupBy === CommonTypes.GroupBy.Environment) || this._stageIdToNameMap) {
            this._createTrendChart();
        }
    };

    private _onUpdateStageIdToNameMap = (stageIdToNameMap: { [key: string]: string }) => {
        this._stageIdToNameMap = stageIdToNameMap;
        if (this._chartData && ConfigurationUtility.areConfValuesEqual(this._confValues, this._chartData.confValues)) {
            this._createTrendChart();
        }
    };

    private _createTrendChart(): void {
        if (this._confValues && this._confValues.groupBy && this._confValues.groupBy === CommonTypes.GroupBy.Environment) {
            const primaryChartData: { [key: string]: ITrendChartAggregateData[] } = this._chartData.primaryChartData;
            const updatedChartData: { [key: string]: ITrendChartAggregateData[] } = {};
            Object.keys(primaryChartData).forEach(k => {
                const key: string =
                    k && this._stageIdToNameMap && this._stageIdToNameMap[k]
                        ? this._stageIdToNameMap[k]
                        : StringFormattingUtility.getDeletedEnvironmentDefIdDisplayString(k);
                updatedChartData[key] = primaryChartData[k];
            });

            this._chartData.primaryChartData = updatedChartData;
        }

        let normalizedDuration: INormalizedDuration | null = null;
        if (this._state.metricSelected === CommonTypes.Metric.AvgDuration) {
            normalizedDuration = this._updateAvgDurationChartData();
        }

        this._state.chartOptions = this._trendChartHelper.getChartOptions(this._chartData, false);
        this._state.chartOptions!.title = this._state.metricSelected !== undefined && 
            this._reportConfigurationDef.getChartMetricsProps().options[this._state.metricSelected] ? 
                this._reportConfigurationDef.getChartMetricsProps().options[this._state.metricSelected] : Resources.TrendChartDefaultAriaLabelText;
        // Update chartOption: Primary Y - Axis Label after updating chart with data
        if (normalizedDuration) {
            this._state.chartOptions!.yAxis!.title = this._getAvgDurationChartTitle(normalizedDuration.durationUnitString);

            // Update chartOptions: GroupBy None should reflect selected duration unit
            if (this._confValues && this._confValues.groupBy === CommonTypes.GroupBy.None) {
                this._state.chartOptions!.series[0].name = normalizedDuration.durationUnitString
                    ? this._getAvgDurationChartTitle(normalizedDuration.durationUnitString)
                    : this._emptyString;
            }
        }

        this.emitChanged();
    }

    private _getAvgDurationChartTitle(durationUnitString: string): string {
        return Utils_String.localeFormat(Resources.DurationSeriesLabel, durationUnitString);
    }

    private _updateAvgDurationChartData(): INormalizedDuration {
        const updatedChartData: { [key: string]: ITrendChartAggregateData[] } = {};

        const durationNormalizationHelper = new DurationNormalizer();
        let normalizedDuration: INormalizedDuration;
        const durationList: number[][] = [];

        const stackByToIndex: { [key: string]: number } = {};
        let indexCount = 0;

        Object.keys(this._chartData.primaryChartData).forEach(k => {
            stackByToIndex[k] = indexCount++;
            durationList.push(this._chartData.primaryChartData[k].map(d => d.aggrValue));
            updatedChartData[k] = this._chartData.primaryChartData[k];
        });
        // Consider data flattening if groupBy is none (no stacking)
        const considerFlattened: boolean = this._confValues.groupBy === CommonTypes.GroupBy.None;

        normalizedDuration = durationNormalizationHelper.normalizeDurations(durationList, considerFlattened);

        if (normalizedDuration) {
            Object.keys(this._chartData.primaryChartData).forEach(k => {
                const updatedValues: number[] = normalizedDuration.durationValues[stackByToIndex[k]];
                for (let i = 0; i < updatedChartData[k].length; i++) {
                    updatedChartData[k][i].aggrValue = updatedValues[i];
                }
            });
        }
        this._chartData.primaryChartData = updatedChartData;

        return normalizedDuration;
    }
}
